import {IMapShareData} from "./MapShareAPI";
import {L} from "../lt-ng/landcare-toolkit";
import Item = L.tree.Item;
import {ILayerQueryRequest, ILayerQueryService, IRequestAndCancel} from "./layer_query";
import {LTServices} from "../lt-ng/lt-services";
import IMapService = LTServices.IMapService;
import {LTMap} from "../lt-ng/lt-directives";
import IQueryString = LTMap.IQueryString;
import getVisibleScienceLayer = L.tree.getVisibleScienceLayer;
import getVisibleScienceLayers = L.tree.getVisibleScienceLayers;
import getLayerName = L.tree.getLayerName;
import getLayerId = L.tree.getLayerId;
import {_F} from "../utility/func";
import combineFeatures = _F.combineFeatures;
import {Geometry} from "ol/geom";
import {Feature} from "ol";
import {GeoJSON, WKT} from "ol/format";
import {fromExtent} from "ol/geom/Polygon";
import {GeoAppEvents} from "../controllers/events/geoapp_events";


export interface IAreaQueryService<T> {
	activeGeometry : Geometry;
	activeQuery : IRequestAndCancel<T>;
	doQuery(geometry : Geometry, reset : boolean, mapShareData : IMapShareData) : ng.IPromise<T>;
	queryGeometry(geometry : Geometry) : void;
	queryMapExtent(): void;
	iconForClass(classNumber : number, layer?: Item) : string|null;
	queryFeatures(features : Feature[]) : void;
	queriedLayers : Item[];
}

export abstract class AreaQueryService<T> implements IAreaQueryService<T> {
	public static $inject = ['$rootScope', 'layerQueryService', 'landcareMapService', '$q', 'myQueryString.QueryString'];
	public activeGeometry : Geometry;
	public activeShare : IMapShareData;
	public activeQuery : IRequestAndCancel<T>;
	public queriedLayers : Item[] = [];
	protected visibleScienceLayer = null;

	abstract queryCall(requestData : ILayerQueryRequest) : IRequestAndCancel<T>;

	constructor(protected $rootScope : ng.IRootScopeService,
				protected layerQueryService : ILayerQueryService,
				protected mapService : IMapService,
				protected $q : ng.IQService,
				protected queryString : IQueryString) {

		$rootScope.$on('doAreaQuery', (ev, geom : Geometry, mapShareData : IMapShareData) => {
			console.log("AreaQueryService $on doAreaQuery");
			this.activeGeometry = geom;
			this.activeShare = mapShareData;

			this.doQuery(geom, true, mapShareData);
		});

		$rootScope.$on(GeoAppEvents.displayShare, (ev, mapShareData : IMapShareData) => {
			if (mapShareData.geometryWKT) {
				let features: Feature[] = [];
				switch (mapShareData.geometryFormat) {
					case 'GeoJSON':
						let geojsonFmt = new GeoJSON();
						try {
                            features = geojsonFmt.readFeatures(mapShareData.geometryWKT)
						} catch (e) {
							console.error('Failed to parse GeoJSON features.');
                        }
                        break;
					default: // implicit WKT
                        let format = new WKT();
                        let geom = format.readGeometry(mapShareData['GeometryWKT']);
                        if (geom) features = [new Feature(geom)];
				}

				if (features.length) {
					this.mapService.featuresLayer.getSource().clear(true);
					this.mapService.featuresLayer.getSource().addFeatures(features);
					this.queryFeatures(features);
				}
			} else {
				this.activeShare = null;
				this.activeGeometry = null;
			}
		});

		mapService.mapPromise.then((/*map*/) => {
			this.visibleScienceLayer = getVisibleScienceLayer(mapService.treeData);
			mapService.featuresLayer.getSource().on('removefeature', (/*ev*/) => {
				// re-query the layer when features are removed from it.
				this.queryFeatures(mapService.featuresLayer.getSource().getFeatures());
			});
		});

		$rootScope.$on('layerToggled', () => {
			let layer = getVisibleScienceLayer(mapService.treeData);
			let layers = getVisibleScienceLayers(mapService.treeData);
			console.log('layerToggled');
			console.log(layer);
			let listDiffers = layers.length !== this.queriedLayers.length
				|| this.queriedLayers.some((l) => layers.indexOf(l) === -1)
				|| layers.some((l) => this.queriedLayers.indexOf(l) === -1);

			if (listDiffers && this.activeGeometry && layers.length) {
				this.doQuery(this.activeGeometry, true, null);
			}
		});
	}



	queryFeatures(features: Feature[]): void {
        switch (features.length) {
            case 0:
                this.$rootScope.$broadcast('doAreaQuery', null);
                return;
            case 1:
                this.$rootScope.$broadcast('doAreaQuery', features[0].getGeometry());
                break;
            default:
                let multiPoly = combineFeatures(features);
                this.$rootScope.$broadcast('doAreaQuery', multiPoly);
        }
    }

	public iconForClass(classNumber : number, layer: Item = null) {
		if (!layer) {
			layer = this.queriedLayers.length ? this.queriedLayers[0] : null;
		}

		if (layer && layer.layerService) {
			const layerService = layer.layerService;
			return layerService ? L.util.Layer.iconForClass(layerService, classNumber, this.queryString) : null;
		}
		return null;
	}

	public doQuery (geometry : Geometry, reset : boolean = true, mapShareData : IMapShareData) : ng.IPromise<T> {
		console.log('AreaQueryCtrl.doQuery', geometry, reset);
		let visibleScienceLayers = getVisibleScienceLayers(this.mapService.treeData)
				.filter((n) => !n.isCustom);


		if (visibleScienceLayers.length && geometry) {
			if (this.activeQuery && this.activeQuery.cancel) {
				this.activeQuery.cancel.resolve();
			}

			this.queriedLayers = visibleScienceLayers;
			this.activeGeometry = geometry;
			let requestData : ILayerQueryRequest = {
				geometry : geometry,
				scienceLayers: visibleScienceLayers.map(getLayerName),
				layerIds: visibleScienceLayers.map(getLayerId),
				epsg: "2193"
			};
			this.activeQuery = this.queryCall(requestData);

		} else {
			this.queriedLayers = [];
			this.activeQuery = null;
			let def = this.$q.defer<T>();
			def.reject();

			this.activeQuery = {
				request : def.promise
			};
		}

		this.$rootScope.$broadcast('newAreaQuery', this.activeQuery, geometry, reset, mapShareData);
		return this.activeQuery.request;
	};


	queryMapExtent(): void {
		let mapService = this.mapService;
		mapService.featuresLayer.getSource().clear(true);
		let extent = mapService.map.getView().calculateExtent(mapService.map.getSize());
		let polygon = fromExtent(extent);
		this.doQuery(polygon, true, null);
	}

	queryGeometry(geometry: Geometry): void {
		console.log("AreaQueryService.queryGeometry");
		this.mapService.featuresLayer.getSource().clear(true);
		this.mapService.featuresLayer.getSource().addFeature(new Feature(geometry));
		this.$rootScope.$broadcast('doAreaQuery', geometry);
	}
}
